/* common application wide utility functions are placed here. */

import moment, { Moment } from "moment-timezone";

import type FixMeLater from "../shared/types/fixMeLater.type";
import { ParkingRequestTypeConstant, ParkingRequestTypeIdConstant, ParkingRequestTypeMap } from "../constants/DatabaseConstants/ParkingConstants";

type FormData = { [key: string]: FixMeLater };

export class General {
  public static formatVersionAndDate = (major: number, minor: number, patch: number, releaseDate: string): string => {
    const formattedVersion = `${major.toString().padStart(2, "0")}.${minor.toString().padStart(2, "0")}.${patch.toString().padStart(2, "0")}`;

    // const releaseDateObj = new Date(releaseDate);
    // const formattedReleaseDate = `${releaseDateObj.getDate().toString().padStart(2, "0")}-${(releaseDateObj.getMonth() + 1).toString().padStart(2, "0")}-${releaseDateObj.getFullYear()}`;

    return `${formattedVersion} (${"24-06-2024"})`;
    // return `${formattedVersion} (${formattedReleaseDate || "24-06-2024"})`;
  };

  public static titleCaseFunction(data: string) {
    return data?.toString().replace(/(^\w)|(\s+\w)/g, (letter) => letter?.toUpperCase());
  }

  public static inputWhiteSpaceRemove(value: FixMeLater) {
    return value.replace(/^\s+/g, "");
  }

  public static firstLetterCapital(data: string) {
    return data?.charAt(0).toUpperCase() + data?.slice(1);
  }

  public static disableFutureDate = (current: Moment | null): boolean => {
    return !!current && current > moment().endOf("day");
  };

  public static searchFilterOnSelect = (input: FixMeLater, option: FixMeLater) => {
    const children = option?.children;

    if (typeof children === "string" || typeof children === "number") {
      return children.toString().toLowerCase().indexOf(input.toLowerCase()) >= 0;
    }

    return false;
  };

  /* method used to check if key-values in obj1 matches with key-values in obj2 */
  public static matchSubset = (obj1: Record<string, FixMeLater>, obj2: Record<string, FixMeLater> | undefined) => {
    if (!obj2) return false;

    return Object.entries(obj1).every(([key, value]) => {
      return obj2[key] === value;
    });
  };

  public static convertDateToAPIFormat = (date: Moment) => {
    return moment(date).format("YYYYMMDD");
  };

  public static convertDateStringToMoment = (dateString: string | undefined | null) => {
    if (!dateString) return null;

    return moment(dateString, "YYYYMMDD");
  };

  /* converts file object to base64 image the return value can be directly consumed by src attributes in img tags.*/
  public static convertFileToBlobUrl = async (file: FixMeLater): Promise<string> => {
    return new Promise((resolve, reject) => {
      try {
        const blobUrl = URL.createObjectURL(file);
        resolve(blobUrl);
      } catch (error) {
        reject(error);
      }
    });
  };

  /* converts base 64 or blob data to file object */
  public static async blobUrlToFile(dataurl: string, fileName: string, fileExtension: string) {
    const response = await fetch(dataurl);
    const blob = await response.blob();
    return new File([blob], fileName, { type: fileExtension });
  }

  public static getGUID = () => {
    return Math.random();
  };

  public static trimFormValues = <T extends FormData>(formData: T): T => {
    const trimmedValues: Partial<T> = {};

    Object.keys(formData).forEach((key) => {
      const value = formData[key];
      trimmedValues[key as keyof T] = typeof value === "string" ? value.trim() : value;
    });

    return trimmedValues as T;
  };

  public static numberWithCommas(x: number | string | null | undefined): string {
    if (x === null || x === undefined) return "0";

    /* Ensure we are working with a string version of the number */
    const numStr = x.toString();

    /* Split the number by the decimal point, if it exists */
    const [integerPart, decimalPart] = numStr.split(".");

    /* If the integer part is longer than 3 digits, format it with commas for thousands */
    const formattedInteger = integerPart.length > 3 ? integerPart.substring(0, integerPart.length - 3).replace(/\B(?=(\d{2})+(?!\d))/g, ",") + "," + integerPart.substring(integerPart.length - 3) : integerPart;

    /* Return the formatted number, adding back any decimal part if it exists */
    return decimalPart ? `${formattedInteger}.${decimalPart}` : formattedInteger;
  }

  public static parseDate = (dateStr: string | null) => (dateStr ? moment(dateStr, "DD-MM-YYYY HH:mm") : null);

  public static getRequestId = (mode: ParkingRequestTypeConstant): ParkingRequestTypeIdConstant => {
    return ParkingRequestTypeMap[mode];
  };

  /* Disable dates after today */
  public static disabledDate = (current: moment.Moment) => {
    return current && current > moment().endOf("day");
  };

  /* Disable dates less than or equal to today */
  public static disabledPastDate = (current: moment.Moment) => {
    return current && current < moment().startOf("day");
  };

  /* Disable times after the current time if the selected date is today */
  public static disabledRangeTime = (date: Moment | null, type: "start" | "end") => {
    if (date && date.isSame(moment(), "day")) {
      const currentHour = moment().hour();
      const currentMinute = moment().minute();
      return {
        disabledHours: () => Array.from({ length: 24 }, (_, i) => i).filter((hour) => hour > currentHour),
        disabledMinutes: (selectedHour: number) => (selectedHour === currentHour ? Array.from({ length: 60 }, (_, i) => i).filter((min) => min > currentMinute) : [])
      };
    }
    return {};
  };

  public static validateDateRange(dateRange: { fromDate: string | null; toDate: string | null }, dateTimeformat = "DD-MM-YYYY HH:mm") {
    try {
      /* Ensure both dates are provided */
      if (!dateRange.fromDate || !dateRange.toDate) {
        throw new Error("Both 'From' and 'To' dates must be provided.");
      }

      const now = moment();

      const fromDate = moment(dateRange.fromDate, dateTimeformat);
      const toDate = moment(dateRange.toDate, dateTimeformat);

      /* Validate if 'fromDate' and 'toDate' are not in the future */
      if (fromDate.isAfter(now) || toDate.isAfter(now)) {
        throw new Error("Future dates and times cannot be selected. Please choose valid dates and times.");
      }

      /*  Validate if 'toDate' is greater than 'fromDate' */
      if (!toDate.isSameOrAfter(fromDate)) {
        throw new Error("'To' date and time must be after 'From' date and time.");
      }
    } catch (error) {
      throw error;
    }
  }

  public static formatTime = (text: string | null): string => {
    return text ? moment(text, "DD-MM-YYYY HH:mm").format("hh:mm A") : "-";
  };

  public static getFormattedDateRange = (selectedDate?: string): { fromDate: string; toDate: string } => {
    const currentDate = moment();
    const selectedDateMoment = selectedDate ? moment(selectedDate, "YYYY-MM-DD") : null;

    if (!selectedDateMoment) {
      return { fromDate: "-", toDate: "-" };
    }

    const isToday = selectedDateMoment.isSame(currentDate, "day");
    const fromDate = selectedDateMoment.startOf("day").format("YYYY-MM-DD HH:mm:ss");
    const toDate = isToday ? currentDate.format("YYYY-MM-DD HH:mm:ss") : selectedDateMoment.endOf("day").format("YYYY-MM-DD HH:mm:ss");

    return { fromDate, toDate };
  };

  public static formatCount = (num: number) => {
    if (num < 1000) return num.toString();
    if (num < 1_000_000) return (num / 1000).toFixed(1).replace(/\.0$/, "") + "k";
    if (num < 1_000_000_000) return (num / 1_000_000).toFixed(1).replace(/\.0$/, "") + "M";
    if (num < 1_000_000_000_000) return (num / 1_000_000_000).toFixed(1).replace(/\.0$/, "") + "B";
    return (num / 1_000_000_000_000).toFixed(1).replace(/\.0$/, "") + "T";
  };
}
